import * as Sentry from '@sentry/react';
import { logger } from '../../lib/logger';

const LIVE_TRACE_FLAG = 'vb_live_trace';

export type LiveDiagnosticSpan = ReturnType<typeof Sentry.startInactiveSpan> | null;

type BreadcrumbDiagnosticValue = string | number | boolean | null | undefined;
type SpanDiagnosticValue = string | number | boolean | undefined;

const isPrimitiveValue = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const normalizeDiagnosticValue = (value: unknown): BreadcrumbDiagnosticValue => {
  if (value == null) return value;
  if (isPrimitiveValue(value)) return value;
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return String(value);
};

const normalizeDiagnosticData = (data: Record<string, unknown>): Record<string, BreadcrumbDiagnosticValue> => {
  const normalized: Record<string, BreadcrumbDiagnosticValue> = {};
  Object.entries(data).forEach(([key, value]) => {
    const nextValue = normalizeDiagnosticValue(value);
    if (nextValue !== undefined) {
      normalized[key] = nextValue;
    }
  });
  return normalized;
};

const normalizeSpanAttributes = (data: Record<string, unknown>): Record<string, SpanDiagnosticValue> => {
  const normalized: Record<string, SpanDiagnosticValue> = {};
  Object.entries(data).forEach(([key, value]) => {
    const nextValue = normalizeDiagnosticValue(value);
    if (nextValue !== undefined && nextValue !== null) {
      normalized[key] = nextValue;
    }
  });
  return normalized;
};

export const isLiveTraceEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (!import.meta.env.DEV) return false;
  try {
    return (
      window.location.search.includes(`${LIVE_TRACE_FLAG}=1`) ||
      window.localStorage.getItem(LIVE_TRACE_FLAG) === '1'
    );
  } catch {
    return window.location.search.includes(`${LIVE_TRACE_FLAG}=1`);
  }
};

export const addLiveRoomDiagnosticBreadcrumb = (
  category: string,
  message: string,
  data: Record<string, unknown> = {},
): void => {
  const normalizedData = normalizeDiagnosticData(data);
  if (isLiveTraceEnabled()) {
    logger.info(`[live-diagnostics:${category}] ${message}`, normalizedData);
  }
  Sentry.addBreadcrumb({
    category: `live-room.${category}`,
    message,
    level: 'info',
    data: normalizedData,
  });
};

export const startLiveRoomDiagnosticSpan = (
  name: string,
  op: string,
  attributes: Record<string, unknown> = {},
): LiveDiagnosticSpan => {
  if (!import.meta.env.PROD && !isLiveTraceEnabled()) {
    return null;
  }

  return Sentry.startInactiveSpan({
    name,
    op,
    attributes: normalizeSpanAttributes(attributes),
  });
};

export const finishLiveRoomDiagnosticSpan = (
  span: LiveDiagnosticSpan,
  attributes: Record<string, unknown> = {},
): void => {
  if (!span) return;
  const normalizedData = normalizeSpanAttributes(attributes);
  Object.entries(normalizedData).forEach(([key, value]) => {
    span.setAttribute(key, value);
  });
  span.end();
};
